import CallIcon from "@mui/icons-material/Call";
import EmailIcon from "@mui/icons-material/Email";
import {
    Alert,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    InputAdornment,
    Tab,
    Tabs,
    TextField,
} from "@mui/material";
import { Formik } from "formik";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import {
    getMessageSeverity,
    getMessageSlice,
    getMessageText,
} from "../../../application/selectors/message";
import { clearMessage, setMessage } from "../../../application/slices/message";
import DefaultApiService from "../../../infrastructure/api/default";
import { wrapFormikHandleChange } from "../../../infrastructure/utils/formatters";
import PanelInfo from "../shared/panel-info";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { message } = useSelector(getMessageSlice);
  const [activeTab, setActiveTab] = useState(0);
  const [verificationTimer, setVerificationTimer] = useState(0);

  useEffect(() => {
    dispatch(clearMessage());
  }, [dispatch]);

  useEffect(() => {
    if (verificationTimer > 0) {
      const timer = setTimeout(() => setVerificationTimer((v) => v - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [verificationTimer]);

  const validationSchema = Yup.object().shape({
    email:
      activeTab === 0
        ? Yup.string()
            .email("ایمیل نامعتبر است")
            .required("لطفا این فیلد را پر کنید.")
        : Yup.string(),
    mobile:
      activeTab === 1
        ? Yup.string()
            .matches(/^09\d{9}$/, "شماره موبایل معتبر نیست")
            .required("لطفا این فیلد را پر کنید.")
        : Yup.string(),
  });

  const initialValues = {
    email: "",
    mobile: "",
    verificationCode: "",
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    dispatch(clearMessage());
  };

  const onSubmit = async (formValue, { setSubmitting }) => {
    setLoading(true);

    try {
      const endpoint =
        activeTab === 0
          ? "account/ForgotPasswordByEmail"
          : "account/ForgotPasswordByMobile";
      const payload =
        activeTab === 0
          ? { email: formValue.email }
          : { mobile: formValue.mobile };

      const response = await DefaultApiService.postApi(endpoint, payload);

      if (response.isSuccess) {
        if (activeTab === 0) {
          dispatch(setMessage("لینک بازیابی رمز عبور به ایمیل شما ارسال شد"));
          setTimeout(() => {
            navigate("/auth/login");
          }, 3000);
        } else {
          dispatch(setMessage("کد تایید به شماره موبایل شما ارسال شد"));
          setVerificationTimer(120); // 2 minutes timer
        }
      } else {
        dispatch(setMessage(response.message || "خطایی رخ داده است"));
      }
    } catch (error) {
      dispatch(
        setMessage(error?.response?.data?.message || "خطایی رخ داده است")
      );
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Card variant="outlined" className="auth-card-container">
      <CardContent>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
          }) => (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item md={12} className="auth-header">
                  <PanelInfo className="auth-logo" />
                  <div className="auth-title">بازیابی رمز عبور</div>
                </Grid>

                <Grid item md={12}>
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    sx={{ mb: 2 }}
                  >
                    <Tab label="بازیابی با ایمیل" />
                    <Tab label="بازیابی با موبایل" />
                  </Tabs>
                </Grid>

                {activeTab === 0 && (
                  <Grid item md={12}>
                    <TextField
                      label="ایمیل"
                      variant="outlined"
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon />
                          </InputAdornment>
                        ),
                      }}
                      name="email"
                      type="email"
                      value={values.email}
                      onChange={wrapFormikHandleChange(handleChange)}
                      error={errors.email && touched.email}
                      helperText={errors.email && touched.email && errors.email}
                    />
                  </Grid>
                )}

                {activeTab === 1 && (
                  <Grid item md={12}>
                    <TextField
                      label="شماره موبایل"
                      variant="outlined"
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CallIcon />
                          </InputAdornment>
                        ),
                      }}
                      name="mobile"
                      type="tel"
                      value={values.mobile}
                      onChange={wrapFormikHandleChange(handleChange)}
                      error={errors.mobile && touched.mobile}
                      helperText={
                        errors.mobile && touched.mobile && errors.mobile
                      }
                    />
                  </Grid>
                )}

                <Grid item md={12}>
                  <Button
                    fullWidth
                    disabled={
                      isSubmitting || (activeTab === 1 && verificationTimer > 0)
                    }
                    variant="contained"
                    type="submit"
                    size="large"
                  >
                    {loading && <CircularProgress size={16} color="inherit" />}
                    {activeTab === 0
                      ? "ارسال لینک بازیابی"
                      : verificationTimer > 0
                      ? `ارسال مجدد کد (${verificationTimer})`
                      : "ارسال کد تایید"}
                  </Button>
                </Grid>

                <Grid item md={12} container justifyContent="center">
                  <Button
                    variant="text"
                    onClick={() => navigate("/auth/login")}
                  >
                    بازگشت به صفحه ورود
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>

        {message && (
          <Alert sx={{ mt: 3 }} severity={getMessageSeverity(message)}>
            {getMessageText(message)}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default ForgotPassword;
